import { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, Send, Loader2, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const suggestions = [
  "What is my next EMI?", "What is my outstanding amount?", "How many EMIs are remaining?",
  "What is my EMI Health Score?", "Explain my loan progress.", "What should I check before my next EMI?",
];

export default function AskAI() {
  const [msgs, setMsgs] = useState([{ role: "ai", text: "Hi! I'm your Ask AI assistant. I can answer questions about your own loans, EMIs, payments and rewards. Ask me anything below." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const loc = useLocation();
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);
  useEffect(() => { if (loc.state?.q) send(loc.state.q); /* eslint-disable-next-line */ }, []);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: q });
      setMsgs((m) => [...m, { role: "ai", text: data.reply, qid: data.question_id, fb: null }]);
    } catch (e) { toast.error(apiError(e)); setMsgs((m) => [...m, { role: "ai", text: "Sorry, I couldn't answer that right now. Please try again shortly." }]); }
    finally { setLoading(false); }
  };

  const sendFb = async (i, qid, helpful) => {
    if (!qid) return;
    setMsgs((m) => m.map((x, j) => (j === i ? { ...x, fb: helpful ? "up" : "down" } : x)));
    try { await api.post("/ai/feedback", { question_id: qid, helpful }); } catch (e) { /* non-blocking */ }
  };

  return (
    <div className="mx-auto flex h-[calc(100vh-9rem)] max-w-3xl flex-col">
      <div className="flex items-center gap-3 border-b border-slate-200 pb-4">
        <span className="grid h-10 w-10 place-items-center rounded-md bg-[#14B8A6] text-white"><Sparkles size={20} /></span>
        <div><h1 className="font-display text-xl font-bold text-[#0B132B]">Ask AI</h1><p className="text-xs text-slate-500">Answers only from your own authorised data • Powered by Claude</p></div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto scroll-thin py-5">
        {msgs.map((m, i) => (
          <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
            <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "rounded-br-sm bg-[#0B132B] text-white" : "rounded-bl-sm border border-slate-200 bg-white text-[#0B132B]"}`} data-testid={`msg-${m.role}`}>{m.text}</div>
            {m.role === "ai" && m.qid && (
              <div className="mt-1 flex items-center gap-2 pl-1" data-testid="ai-feedback">
                {m.fb ? <span className="text-[11px] text-slate-400">Thanks for the feedback!</span> : (<>
                  <span className="text-[11px] text-slate-400">Was this helpful?</span>
                  <button onClick={() => sendFb(i, m.qid, true)} data-testid="ai-fb-up" className="rounded-md border border-slate-200 px-1.5 py-0.5 text-xs hover:border-[#14B8A6]" aria-label="Helpful">👍</button>
                  <button onClick={() => sendFb(i, m.qid, false)} data-testid="ai-fb-down" className="rounded-md border border-slate-200 px-1.5 py-0.5 text-xs hover:border-red-300" aria-label="Not helpful">👎</button>
                </>)}
              </div>
            )}
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="flex items-center gap-2 rounded-2xl rounded-bl-sm border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</div></div>}
        <div ref={endRef} />
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {suggestions.map((s) => (
          <button key={s} onClick={() => send(s)} disabled={loading} data-testid="ai-suggestion" className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-[#0B132B] hover:border-[#14B8A6] hover:text-[#14B8A6] disabled:opacity-50">{s}</button>
        ))}
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about your loans…" className="flex-1 bg-transparent px-3 text-sm outline-none" data-testid="ai-input" />
        <button onClick={() => send()} disabled={loading || !input.trim()} data-testid="ai-send" className="grid h-9 w-9 place-items-center rounded-md bg-[#14B8A6] text-white disabled:opacity-50"><Send size={16} /></button>
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400"><ShieldCheck size={12} /> Your data is never used to answer another customer's questions.</p>
    </div>
  );
}
